import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { DataManager } from '../../services/dataManager';
import {
    generateSmartSummary,
    summarizeDiaryEntry,
    analyzeBioenergeticStructure,
    BIO_PHYSICAL_MARKERS,
    analyzeClientTexts
} from '../../utils/textAnalysis';
import GuidedSynthesisQuestionnaire from './GuidedSynthesisQuestionnaire';
import SemanticMap from './SemanticMap';
import EditableReportModal from '../counselor/EditableReportModal';
import html2pdf from 'html2pdf.js';

const escapeHTML = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
};

const SupervisionView = ({ client, meetings, works, onClientUpdate, counselor, onOpenTranscript, initialContractId = null }) => {
    const [downloading, setDownloading] = useState(false);
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [showEditableReportModal, setShowEditableReportModal] = useState(false);
    const [questionnaireData, setQuestionnaireData] = useState(null);
    const [selectedMarkers, setSelectedMarkers] = useState(client.bioPhysicalMarkers || []);

    const contracts = useMemo(() => DataManager.getClientContracts(client), [client]);
    const [selectedContractId, setSelectedContractId] = useState(initialContractId || (contracts.length > 0 ? (contracts.find(c => c.status === 'attivo')?.id || contracts[0].id) : 'all'));

    useEffect(() => {
        if (client?.id) {
            DataManager.getQuestionnaireAnswers(client.id).then(setQuestionnaireData);
        }
    }, [client?.id]);

    const toggleMarker = (markerId) => {
        setSelectedMarkers(prev => prev.includes(markerId) ? prev.filter(m => m !== markerId) : [...prev, markerId]);
    };

    const selectedContract = useMemo(() => {
        return contracts.find(c => c.id === selectedContractId) || null;
    }, [contracts, selectedContractId]);

    const activeMeetings = useMemo(() => {
        if (!selectedContractId || selectedContractId === 'all') return meetings;
        const filtered = meetings.filter(m => m.contractId === selectedContractId);
        return filtered.length > 0 ? filtered : meetings; // Fallback to all if none tagged yet
    }, [meetings, selectedContractId]);

    // LOGICA INTRO E ANAGRAFICA
    const introInfo = useMemo(() => {
        const parts = [client.name];
        let eta = client.age;
        if (!eta && client.birthDate) {
            const dob = new Date(client.birthDate);
            const diffMs = Date.now() - dob.getTime();
            const ageDt = new Date(diffMs); 
            const calcAge = Math.abs(ageDt.getUTCFullYear() - 1970);
            if (!isNaN(calcAge)) eta = `${calcAge}`;
        }
        const lavoro = client.profession || client.workSituation;
        if (eta) parts.push(`${eta} anni`);
        if (lavoro) parts.push(lavoro);
        if (selectedContract?.title) {
            parts.push(`Contratto: "${selectedContract.title}"`);
        } else if (client.contract?.title) {
            parts.push(`Tema: "${client.contract.title}"`);
        }
        return parts.join(', ');
    }, [client, selectedContract]);

    // ANALISI PAROLE CHIAVE DINAMICA
    const supervisionKeywords = useMemo(() => {
        return analyzeClientTexts(works, activeMeetings, 60);
    }, [works, activeMeetings]);

    // ANALISI BIOENERGETICA DINAMICA
    const bioAnalysis = useMemo(() => {
        return analyzeBioenergeticStructure(works, activeMeetings, selectedMarkers);
    }, [works, activeMeetings, selectedMarkers]);

    // CONTEGGIO STRUMENTI CLINICI
    const toolStats = useMemo(() => {
        const stats = { breathing: 0, cards: 0, frequencies: 0, dreams: 0, experiments: 0 };
        if (works) {
            works.forEach(w => {
                if (w.type === 'breathing_session') stats.breathing++;
                if (w.type === 'creative_cards_session') stats.cards++;
                if (w.type === 'frequency_feedback') stats.frequencies++;
                if (w.type === 'dream_analysis') stats.dreams++;
                if (w.type === 'somatic_experiment' || w.type === 'at_experiment' || w.type === 'drama_triangle') stats.experiments++;
            });
        }
        if (meetings) {
            meetings.forEach(m => {
                // 1. Explicit exercises checkboxes
                const exercises = m.observations?.exercises || m.exercises || [];
                exercises.forEach(ex => {
                    if (ex === 'respirazione' || ex === 'body_scan') stats.breathing++;
                    if (ex === 'carte_creative') stats.cards++;
                    if (ex === 'frequenze') stats.frequencies++;
                    if (ex === 'sogno') stats.dreams++;
                    if (ex === 'sedia_vuota' || ex === 'scrittura' || ex === 'baseline') stats.experiments++;
                });

                // 2. Explicit legacy techniques string
                if (m.observations?.techniques && typeof m.observations.techniques === 'string') {
                    const techStr = m.observations.techniques.toLowerCase();
                    if (techStr.includes('respirazion') || techStr.includes('respiro') || techStr.includes('body scan') || techStr.includes('grounding')) stats.breathing++;
                    if (techStr.includes('carte') || techStr.includes('carta')) stats.cards++;
                    if (techStr.includes('frequenz')) stats.frequencies++;
                    if (techStr.includes('sogno') || techStr.includes('oniric')) stats.dreams++;
                    if (techStr.includes('sedia vuota') || techStr.includes('scrittur') || techStr.includes('esperiment')) stats.experiments++;
                }

                // 3. Scan note text for tool mentions (if they weren't explicitly checked above to avoid duplicate counts in the same meeting)
                const noteText = (m.counselorNotes || m.notes || '').toLowerCase();
                if (noteText) {
                    const hasExplicitBreathing = exercises.includes('respirazione') || exercises.includes('body_scan') || (m.observations?.techniques && m.observations.techniques.toLowerCase().includes('respirazion'));
                    const hasExplicitCards = exercises.includes('carte_creative') || (m.observations?.techniques && m.observations.techniques.toLowerCase().includes('carte'));
                    const hasExplicitFreq = exercises.includes('frequenze') || (m.observations?.techniques && m.observations.techniques.toLowerCase().includes('frequenz'));
                    const hasExplicitDream = exercises.includes('sogno') || (m.observations?.techniques && m.observations.techniques.toLowerCase().includes('sogno'));
                    const hasExplicitExp = exercises.includes('sedia_vuota') || exercises.includes('scrittura') || exercises.includes('baseline') || (m.observations?.techniques && m.observations.techniques.toLowerCase().includes('sedia vuota'));

                    if (!hasExplicitBreathing && (noteText.includes('respirazione') || noteText.includes('respiro circolare') || noteText.includes('body scan') || noteText.includes('grounding'))) {
                        stats.breathing++;
                    }
                    if (!hasExplicitCards && (noteText.includes('carte creative') || noteText.includes('carta creativa') || noteText.includes('carte proiettive'))) {
                        stats.cards++;
                    }
                    if (!hasExplicitFreq && (noteText.includes('frequenze acustiche') || noteText.includes('frequenze binaurali') || noteText.includes('campane tibetane'))) {
                        stats.frequencies++;
                    }
                    if (!hasExplicitDream && (noteText.includes('lavoro onirico') || noteText.includes('analisi del sogno') || noteText.includes('racconto del sogno'))) {
                        stats.dreams++;
                    }
                    if (!hasExplicitExp && (noteText.includes('sedia vuota') || noteText.includes('esperimento somatico') || noteText.includes('esperimento gestalt') || noteText.includes('dialogo delle parti'))) {
                        stats.experiments++;
                    }
                }
            });
        }
        return stats;
    }, [works, meetings]);

    const diaryByDate = useMemo(() => {
        const entries = [];
        if (works) {
            works.forEach(w => {
                if (['diary', 'journal', 'dream', 'at_experiment', 'somatic_experiment'].includes(w.type)) {
                    entries.push({ ...w, source: 'client', timestamp: w.timestamp, displayType: w.type === 'dream' ? '💭 Sogno' : '📝 Diario' });
                }
            });
        }
        if (meetings) {
            meetings.forEach(m => {
                if (m.counselorNotes || m.notes) {
                    entries.push({ ...m, timestamp: m.date, source: 'counselor', displayType: '🛋️ Nota Counselor', content: m.counselorNotes || m.notes });
                }
            });
        }
        entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        const grouped = {};
        entries.forEach(entry => {
            const date = new Date(entry.timestamp).toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' });
            if (!grouped[date]) grouped[date] = [];
            grouped[date].push(entry);
        });
        return grouped;
    }, [works, meetings]);

    // EXPORT PDF PROFESSIONALE A4
    const handleExport = async () => {
        setDownloading(true);
        const reportDate = new Date().toLocaleDateString('it-IT');
        const questionnaireData = await DataManager.getQuestionnaireAnswers(client.id);
        const qAnswers = questionnaireData?.answers || {};
        const counselorName = counselor?.name || "Counselor";

        // Calcolo pesi distretti corporei per grafica somatica
        const districtWeights = { 'Testa': 0, 'Tronco': 0, 'Bacino': 0, 'Arti': 0 };
        BIO_PHYSICAL_MARKERS.forEach(d => {
            const count = d.markers.filter(m => selectedMarkers.includes(m.id)).length;
            if (d.district.includes('Testa') || d.district.includes('Occhi') || d.district.includes('Bocca')) districtWeights['Testa'] += count;
            else if (d.district.includes('Torace')) districtWeights['Tronco'] += count;
            else if (d.district.includes('Bacino')) districtWeights['Bacino'] += count;
            else districtWeights['Arti'] += count;
        });

        const html = `
        <!DOCTYPE html>
        <html>
        <head>
            <style>
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&display=swap');
                * { box-sizing: border-box; -webkit-print-color-adjust: exact; }
                body { font-family: 'Inter', sans-serif; color: #1e293b; margin: 0; padding: 0; background: #fff; }
                .page { width: 210mm; height: 296mm; padding: 20mm; margin: 0 auto; position: relative; overflow: hidden; }
                .header { border-bottom: 3px solid #4338ca; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: center; }
                h1 { font-size: 26px; font-weight: 800; color: #1e1b4b; margin: 0; text-transform: uppercase; letter-spacing: -0.02em; }
                .section { margin-bottom: 30px; }
                .section-title { font-size: 11px; font-weight: 800; color: #4338ca; text-transform: uppercase; letter-spacing: 0.15em; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 15px; }
                .card { background: #f8fafc; border-radius: 16px; padding: 20px; border: 1px solid #f1f5f9; }
                .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .stat-box { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; text-align: center; }
                .badge-bio { background: #e0f2fe; color: #0369a1; font-size: 10px; font-weight: 800; padding: 4px 10px; rounded-full; text-transform: uppercase; }
                .footer { position: absolute; bottom: 15mm; left: 20mm; right: 20mm; border-top: 1px solid #e2e8f0; padding-top: 10px; font-size: 9px; color: #94a3b8; display: flex; justify-content: space-between; font-weight: 600; }
                .page-break { page-break-before: always; }
                .bar-bg { width: 100%; height: 8px; background: #f1f5f9; border-radius: 4px; margin-top: 5px; overflow: hidden; }
                .bar-fg { height: 100%; background: #4338ca; border-radius: 4px; }
            </style>
        </head>
        <body>
            <div class="page">
                <div class="header">
                    <div><h1>AwarenessTool</h1><p style="font-size: 10px; font-weight: 800; color: #6366f1; margin-top: 4px; letter-spacing: 0.2em;">SUPERVISIONE PROFESSIONALE BIOGESTALT</p></div>
                    <div style="text-align: right;"><h2 style="font-size: 18px; font-weight: 800; margin: 0; color: #1e293b;">${escapeHTML(client.name)}</h2><p style="font-size: 10px; color: #94a3b8; font-weight: 600;">Report del ${reportDate}</p></div>
                </div>

                <div class="section">
                    <div class="section-title">1. Inquadramento e Contratto</div>
                    <div class="grid-2">
                        <div class="card">
                            <p style="font-size: 12px; margin-bottom: 8px;"><strong>Anagrafica:</strong> ${escapeHTML(qAnswers.age || client.age || 'N/D')} anni, ${escapeHTML(qAnswers.profession || client.profession || 'N/D')}</p>
                            <p style="font-size: 12px; margin-bottom: 8px;"><strong>Contratto:</strong> ${escapeHTML(client.contract?.title || 'Definito in sessione')}</p>
                            <p style="font-size: 12px;"><strong>Sessioni totali:</strong> <span style="color:#4338ca; font-weight:800;">${meetings.length} incontri</span></p>
                        </div>
                        <div class="card" style="background:#eef2ff; border-color:#e0e7ff;">
                            <p style="font-size: 11px; font-weight:800; color:#4338ca; margin-bottom:5px; text-transform:uppercase;">Focus Somatico Rilevato</p>
                            ${Object.entries(districtWeights).map(([d, w]) => `
                                <div style="margin-bottom:6px;">
                                    <div style="display:flex; justify-content:space-between; font-size:9px; font-weight:700;"><span>${d}</span><span>${w} marker</span></div>
                                    <div class="bar-bg"><div class="bar-fg" style="width:${Math.min(w * 20, 100)}%"></div></div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">2. Analisi Struttura Caratteriale</div>
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px;">
                        ${bioAnalysis.slice(0, 3).map(item => `
                            <div class="card" style="padding:15px; border-left: 4px solid #4338ca;">
                                <div style="display:flex; justify-content:space-between; align-items:start; margin-bottom:8px;">
                                    <strong style="font-size:13px;">${item.name}</strong>
                                    <span style="color:#4338ca; font-weight:800; font-size:14px;">${item.probability}%</span>
                                </div>
                                <p style="font-size:9px; font-weight:800; color:#6366f1; text-transform:uppercase; margin-bottom:5px;">Ferita: ${item.wound}</p>
                                <p style="font-size:10px; color:#475569; line-height:1.4;">${escapeHTML(item.description).substring(0, 120)}...</p>
                            </div>
                        `).join('')}
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">3. Utilizzo Strumenti di Counseling</div>
                    <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px;">
                        <div class="stat-box"><div style="font-size:18px;">🌬️</div><div style="font-weight:800; font-size:14px;">${toolStats.breathing}</div><div style="font-size:7px; font-weight:800; color:#94a3b8;">RESPIRO</div></div>
                        <div class="stat-box"><div style="font-size:18px;">🎴</div><div style="font-weight:800; font-size:14px;">${toolStats.cards}</div><div style="font-size:7px; font-weight:800; color:#94a3b8;">CARTE</div></div>
                        <div class="stat-box"><div style="font-size:18px;">🎵</div><div style="font-weight:800; font-size:14px;">${toolStats.frequencies}</div><div style="font-size:7px; font-weight:800; color:#94a3b8;">FREQ.</div></div>
                        <div class="stat-box"><div style="font-size:18px;">🌙</div><div style="font-weight:800; font-size:14px;">${toolStats.dreams}</div><div style="font-size:7px; font-weight:800; color:#94a3b8;">SOGNI</div></div>
                        <div class="stat-box"><div style="font-size:18px;">🧪</div><div style="font-weight:800; font-size:14px;">${toolStats.experiments}</div><div style="font-size:7px; font-weight:800; color:#94a3b8;">LAB/EXP</div></div>
                    </div>
                </div>

                <div class="footer"><span>CONFIDENZIALE - SOLO PER USO PROFESSIONALE</span><span>Pagina 1 di 2</span></div>
            </div>

            <div class="page-break"></div>

            <div class="page">
                <div class="section">
                    <div class="section-title">4. Mappa Semantica</div>
                    <p style="font-size:11px; color:#475569; margin-bottom:12px; line-height:1.6;">
                        La mappa semantica di <strong>${escapeHTML(client.name)}</strong> ha come parole più ricorrenti:
                    </p>
                    <div style="display:flex; flex-wrap:wrap; gap:8px;">
                        ${Object.entries(supervisionKeywords).sort((a,b) => b[1].count - a[1].count).slice(0, 18).map(([word, data]) => `
                            <span style="background:#eef2ff; color:#4338ca; border:1px solid #c7d2fe; border-radius:20px; padding:4px 12px; font-size:10px; font-weight:700;">
                                ${escapeHTML(data.display || word)} <span style="opacity:0.6; font-size:9px;">(${data.count})</span>
                            </span>
                        `).join('')}
                    </div>
                </div>

                <div class="grid-2" style="margin-bottom:25px;">
                    <div class="section">
                        <div class="section-title">5. Utilizzo App da Parte del Cliente</div>
                        ${(() => {
                            const clientWorks = works ? works.filter(w => ['diary','journal','dream','at_experiment','somatic_experiment'].includes(w.type)) : [];
                            const breathingWorks = works ? works.filter(w => w.type === 'breathing_session') : [];
                            const totalAppUse = clientWorks.length + breathingWorks.length;
                            if (totalAppUse === 0) {
                                return `<p style="font-size:11px; color:#94a3b8; font-style:italic;">Il cliente non ha utilizzato l'app in autonomia.</p>`;
                            }
                            return `
                                <div class="card" style="padding:12px;">
                                    <p style="font-size:12px; margin-bottom:6px;"><strong>✅ Sì</strong> — il cliente ha utilizzato l'app in autonomia</p>
                                    <p style="font-size:11px; color:#475569; margin-bottom:4px;">📖 Diari/Note: <strong>${clientWorks.length}</strong> registrazioni</p>
                                    <p style="font-size:11px; color:#475569;">🌬️ Respirazioni: <strong>${breathingWorks.length}</strong> sessioni</p>
                                </div>
                            `;
                        })()}
                    </div>

                    <div class="section">
                        <div class="section-title">6. Esercizi e Tecniche</div>
                        <div class="card" style="padding:12px;">
                            ${(() => {
                                const toolIdToLabel = { respirazione:'Respirazione', carte_creative:'Carte Creative', frequenze:'Frequenze', sogno:'Lavoro Onirico', sedia_vuota:'Sedia Vuota', baseline:'Test Baseline', body_scan:'Grounding / Body Scan', scrittura:'Scrittura' };
                                const techniqueSet = new Set();
                                if (meetings) {
                                    meetings.forEach(m => {
                                        const ex = m.observations?.exercises || m.exercises || [];
                                        ex.forEach(e => techniqueSet.add(toolIdToLabel[e] || e));
                                        if (m.observations?.techniques && typeof m.observations.techniques === 'string') {
                                            m.observations.techniques.split(',').map(t => t.trim()).filter(Boolean).forEach(t => techniqueSet.add(t));
                                        }
                                    });
                                }
                                const techs = [...techniqueSet];
                                if (techs.length === 0) return `<p style="font-size:11px; color:#94a3b8; font-style:italic;">Nessuna tecnica specifica registrata.</p>`;
                                return techs.map(t => `<span style="display:inline-block; background:#f1f5f9; border-radius:8px; padding:3px 10px; font-size:10px; font-weight:700; margin:3px 3px 0 0; color:#334155;">${escapeHTML(t)}</span>`).join('');
                            })()}
                        </div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">8. Voci Significative</div>
                    <div style="display:flex; flex-direction:column; gap:10px;">
                        ${(() => {
                            const quotes = [];
                            // Da note counselor
                            if (meetings) {
                                meetings.forEach(m => {
                                    const text = m.counselorNotes || m.notes || '';
                                    const sentences = text.split(/[.!?\n]+/).map(s => s.trim()).filter(s => s.length > 20 && s.length < 180);
                                    sentences.slice(0, 1).forEach(s => quotes.push({ text: s, source: 'Nota Counselor' }));
                                });
                            }
                            // Da diari cliente
                            if (works) {
                                works.filter(w => ['diary','journal'].includes(w.type)).forEach(w => {
                                    const text = typeof w.content === 'string' ? w.content : (w.content?.text || '');
                                    const sentences = text.split(/[.!?\n]+/).map(s => s.trim()).filter(s => s.length > 15 && s.length < 150);
                                    sentences.slice(0, 1).forEach(s => quotes.push({ text: s, source: 'Diario Cliente' }));
                                });
                            }
                            if (quotes.length === 0) return `<p style="font-size:11px; color:#94a3b8; font-style:italic;">Nessuna citazione disponibile.</p>`;
                            return quotes.slice(0, 6).map(q => `
                                <div style="border-left:3px solid #6366f1; padding:8px 14px; background:#f8fafc; border-radius:0 8px 8px 0;">
                                    <p style="font-size:11px; color:#1e293b; font-style:italic; line-height:1.5; margin:0 0 4px 0;">"${escapeHTML(q.text)}"</p>
                                    <span style="font-size:8px; font-weight:800; color:#94a3b8; text-transform:uppercase;">${escapeHTML(q.source)}</span>
                                </div>
                            `).join('');
                        })()}
                    </div>
                </div>

                <div class="footer"><span>AwarenessTool Supervision Engine</span><span>Pagina 2 di 3</span></div>
            </div>

            <div class="page-break"></div>

            <div class="page">
                <div class="header">
                    <div><h1>AwarenessTool</h1><p style="font-size: 10px; font-weight: 800; color: #6366f1; margin-top: 4px; letter-spacing: 0.2em;">SUPERVISIONE PROFESSIONALE BIOGESTALT</p></div>
                    <div style="text-align: right;"><h2 style="font-size: 18px; font-weight: 800; margin: 0; color: #1e293b;">${escapeHTML(client.name)}</h2><p style="font-size: 10px; color: #94a3b8; font-weight: 600;">Report del ${reportDate}</p></div>
                </div>

                <div class="section">
                    <div class="section-title">9. Il percepito di ${escapeHTML(counselorName)} durante gli incontri</div>
                    <div style="display:flex; flex-direction:column; gap:10px;">
                        ${(() => {
                            const feelings = meetings.filter(m => m.counselorFeeling && m.counselorFeeling.trim() && !m.deletedAt);
                            if (feelings.length === 0) {
                                return `<p style="font-size:11px; color:#94a3b8; font-style:italic;">Nessun percepito registrato dal counselor per questo periodo.</p>`;
                            }
                            return feelings.map(m => {
                                const formattedDate = (() => {
                                    const d = m.date;
                                    if (!d) return 'Data N/D';
                                    if (typeof d === 'string' && d.includes('/')) return d;
                                    try { return new Date(d).toLocaleDateString('it-IT'); } catch (e) { return d; }
                                })();
                                return `
                                    <div style="border-left:3px solid #10b981; padding:8px 14px; background:#f0fdf4; border-radius:0 8px 8px 0;">
                                        <span style="font-size:9px; font-weight:800; color:#059669; text-transform:uppercase; display:block; margin-bottom:4px;">Incontro del ${escapeHTML(formattedDate)}</span>
                                        <p style="font-size:11px; color:#1e293b; font-style:italic; line-height:1.5; margin:0;">"${escapeHTML(m.counselorFeeling)}"</p>
                                    </div>
                                `;
                            }).join('');
                        })()}
                    </div>
                </div>

                <div class="footer"><span>AwarenessTool Supervision Engine</span><span>Pagina 3 di 3</span></div>
            </div>
        </body>
        </html>`;

        try {
            const opt = {
                margin: 0,
                filename: `Supervisione_${client.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, windowWidth: 794 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };
            const element = document.createElement('div');
            element.innerHTML = html.trim();
            await html2pdf().from(element).set(opt).save();
        } catch (e) {
            console.error("Export Error:", e);
        } finally {
            setDownloading(false);
        }
    };

    // UI RENDER
    return (
        <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-slate-800">Supervisione Professionale</h1>
                        <p className="text-slate-500 font-medium mt-0.5">{introInfo}</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {onOpenTranscript && (
                            <button
                                onClick={onOpenTranscript}
                                className="px-5 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-2xl font-bold transition active:scale-95 shadow-sm flex items-center gap-2"
                                title="Visualizza e consulta la trascrizione clinica integrale di tutti gli incontri del cliente"
                            >
                                <span>📋</span>
                                <span>Trascrizione Incontri</span>
                            </button>
                        )}
                        <button
                            onClick={() => setShowEditableReportModal(true)}
                            className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-bold transition active:scale-95 shadow-lg flex items-center gap-2"
                            title="Revisiona, modifica ed esporta il report completo di supervisione"
                        >
                            <span>📄</span>
                            <span>Esporta / Modifica Report</span>
                        </button>
                    </div>
                </div>

                {/* Contract Selector for Supervision */}
                {contracts.length > 0 && (
                    <div className="pt-3 border-t border-slate-100 flex items-center gap-2 overflow-x-auto pb-1">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider shrink-0 flex items-center gap-1">
                            <span>📑</span> Contratto:
                        </span>
                        <button
                            onClick={() => setSelectedContractId('all')}
                            className={`px-3 py-1 rounded-xl text-xs font-bold transition shrink-0 ${selectedContractId === 'all' ? 'bg-slate-900 text-white shadow-xs' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        >
                            Tutti gli Incontri ({meetings.length})
                        </button>
                        {contracts.map(c => {
                            const cMeetings = meetings.filter(m => m.contractId === c.id);
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => setSelectedContractId(c.id)}
                                    className={`px-3 py-1 rounded-xl text-xs font-bold transition shrink-0 flex items-center gap-1.5 ${selectedContractId === c.id ? 'bg-indigo-600 text-white shadow-xs' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                >
                                    <span>{c.status === 'attivo' ? '🟢' : '⚪'}</span>
                                    <span>{c.title}</span>
                                    <span className="text-[10px] opacity-80">({cMeetings.length > 0 ? cMeetings.length : 'tutti'})</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <GuidedSynthesisQuestionnaire client={client} meetings={activeMeetings} works={works} onClientUpdate={onClientUpdate} />

            <div className="bg-white border border-slate-200 rounded-[2.5rem] p-10 shadow-sm">
                <SemanticMap keywords={supervisionKeywords} clientId={client.id} onUpdate={onClientUpdate} />
            </div>

            <div className="bg-white border border-slate-200 rounded-[2.5rem] p-10 shadow-sm">
                <div className="flex justify-between items-center mb-10">
                    <h3 className="text-2xl font-black text-slate-800">Analisi Caratteriale Bioenergetica</h3>
                    <div className="flex gap-2">
                        <button onClick={() => setIsEditingBio(!isEditingBio)} className="text-xs font-black text-indigo-600 uppercase bg-indigo-50 px-4 py-2 rounded-full border border-indigo-100 hover:bg-indigo-100">
                            {isEditingBio ? 'Chiudi' : 'Calibrazione Somatica'}
                        </button>
                    </div>
                </div>

                {isEditingBio && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 p-8 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                        {BIO_PHYSICAL_MARKERS.map(district => (
                            <div key={district.district}>
                                <h5 className="text-[10px] font-black text-slate-400 uppercase mb-3">{district.district}</h5>
                                <div className="flex flex-wrap gap-2">
                                    {district.markers.map(m => (
                                        <button
                                            key={m.id}
                                            onClick={() => toggleMarker(m.id)}
                                            className={`px-3 py-1.5 rounded-xl text-[10px] font-bold border-2 transition-colors ${selectedMarkers.includes(m.id) ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-500 border-slate-200 hover:border-emerald-300'}`}
                                        >
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div className="col-span-full flex justify-between items-center mt-4">
                            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Seleziona i marker rilevati e clicca "Salva"</p>
                            <button onClick={async () => {
                                await DataManager.updateClient(client.id, { bioPhysicalMarkers: selectedMarkers });
                                setIsEditingBio(false);
                                if (onClientUpdate) onClientUpdate();
                            }} className="bg-emerald-600 text-white px-8 py-2 rounded-xl font-bold shadow-sm hover:bg-emerald-700">Salva Mappa Somatica</button>
                        </div>
                    </div>
                )}

                {bioAnalysis && bioAnalysis.length > 0 && bioAnalysis[0].probability > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {bioAnalysis.filter(item => item.probability > 0).map((item, idx) => (
                            <div key={item.id} className={`p-6 rounded-3xl border transition-all ${idx === 0 ? 'bg-indigo-50 border-indigo-200 shadow-md ring-2 ring-indigo-100' : 'bg-slate-50 border-slate-100'}`}>
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        {idx === 0 && <span className="text-[8px] font-black uppercase bg-indigo-600 text-white px-2 py-0.5 rounded-full mb-1 inline-block">Dominante</span>}
                                        <h4 className={`font-black ${idx === 0 ? 'text-indigo-900 text-xl' : 'text-slate-800'}`}>{item.name}</h4>
                                    </div>
                                    <span className={`text-xl font-black ${idx === 0 ? 'text-indigo-700' : 'text-slate-600'}`}>{item.probability}%</span>
                                </div>
                                <p className="text-[10px] font-bold uppercase mb-2 tracking-wide text-emerald-600">Ferita: {item.wound}</p>
                                <p className="text-xs italic leading-relaxed text-slate-600 mb-4">{item.description}</p>
                                {(item.identifiedMarkers?.length > 0 || item.matchedPhysicalMarkers?.length > 0) && (
                                    <div className="border-t border-slate-200 pt-3 mt-3">
                                        <p className="text-[9px] font-bold uppercase text-slate-400 mb-2">Indicatori Rilevati:</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {item.identifiedMarkers?.slice(0, 5).map((m, i) => (
                                                <span key={`text-${i}`} className="px-2 py-1 bg-white text-slate-500 text-[9px] rounded-md border border-slate-200">"{m}"</span>
                                            ))}
                                            {item.matchedPhysicalMarkers?.map((m, i) => (
                                                <span key={`phys-${i}`} className="px-2 py-1 bg-emerald-50 text-emerald-600 text-[9px] rounded-md border border-emerald-100">Soma: {m}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-slate-50 border border-dashed border-slate-200 p-10 rounded-[2rem] text-center flex flex-col items-center justify-center">
                        <span className="text-4xl mb-4 opacity-50">🧬</span>
                        <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Dati Insufficienti</p>
                        <p className="text-xs text-slate-400 mt-2 max-w-md">Aggiungi note ai lavori del cliente o usa la Calibrazione Somatica per generare l'analisi bioenergetica locale basata sugli indicatori.</p>
                    </div>
                )}
            </div>

            {/* Editable Report Modal */}
            {showEditableReportModal && (
                <EditableReportModal
                    client={client}
                    meetings={activeMeetings}
                    works={works}
                    counselor={counselor}
                    supervisionKeywords={supervisionKeywords}
                    bioAnalysis={bioAnalysis}
                    toolStats={toolStats}
                    questionnaireAnswers={questionnaireData?.answers || {}}
                    initialContractId={selectedContractId}
                    onClose={() => setShowEditableReportModal(false)}
                />
            )}
        </div>
    );
};

export default SupervisionView;